/**
 * URL paths (relative)
 * These paths are combined with environment-specific base URLs
 */
const LOGIN_URL = '/login-redirect';
const LOGOUT_CALLBACK_URL = '/logout';

/**
 * Localized URL paths
 */
const LOCALIZED_URLS: Record<string, Record<string, string>> = {
  termsAndConditions: {
    fr: '/conditions-generales',
  },
  privacyPolicy: {
    fr: '/politique-vie-privee',
  },
};

const DEFAULT_LOCALE = 'fr';

/**
 * Environment configuration for application URLs
 * Maps Auth0 tenant domains to their corresponding application URLs
 */

interface EnvironmentConfig {
  /** Base URL of the main application */
  appBaseUrl: string;
  /** Environment name for debugging */
  name: string;
  /**
   * Cookie `domain` attribute for cross-subdomain cookies (e.g. consent state
   * shared with the shell SPA). Set on hosted envs that share a TLD; omitted
   * in local dev because `localhost` does not support the domain attribute.
   */
  cookieDomain?: string;
}

/**
 * Mapping of Auth0 tenant domains to application configurations
 * The key is the hostname of the Auth0 tenant (window.location.hostname in ACUL)
 */
const ENVIRONMENT_MAP: Record<string, EnvironmentConfig> = {
  // Production
  'particuliers.engie.fr': {
    appBaseUrl: 'https://www.engie.fr',
    name: 'prod',
    cookieDomain: '.engie.fr',
  },
  // QUA
  'nova-acc-particuliers.engie.fr': {
    appBaseUrl: 'https://nova-acc-particuliers.engie.fr',
    name: 'acc',
    cookieDomain: '.engie.fr',
  },
  // Int
  'nova-int-particuliers.engie.fr': {
    appBaseUrl: 'https://nova-int-particuliers.engie.fr/',
    name: 'int',
    cookieDomain: '.engie.fr',
  },
  // Development (localhost or dev tenant)
  'nova-dev-particuliers.engie.fr': {
    appBaseUrl: 'http://localhost:3000',
    name: 'dev',
  },
};

/**
 * Default configuration when tenant is not recognized
 */
const DEFAULT_CONFIG: EnvironmentConfig = {
  appBaseUrl: 'https://www.engie.fr',
  name: 'default',
};

/**
 * Get the current environment configuration based on the Auth0 tenant domain
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const hostname = window.location.hostname;
  return ENVIRONMENT_MAP[hostname] ?? DEFAULT_CONFIG;
}

/**
 * Get the application base URL for the current environment
 */
export function getAppBaseUrl(): string {
  return getEnvironmentConfig().appBaseUrl;
}

/**
 * Get the current environment name
 */
export function getEnvironmentName(): string {
  return getEnvironmentConfig().name;
}

/**
 * Build a full URL for the application with the given path
 * @param path - The path to append to the base URL (e.g., '/login-redirect')
 * @param locale - Locale to include in the URL (e.g., 'fr')
 */
export function buildAppUrl(path: string, locale: string): string {
  const baseUrl = getAppBaseUrl();
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}/${locale}${normalizedPath}`;
}

/**
 * Get a localized URL path
 * @param key - The URL key (e.g., 'termsAndConditions', 'privacyPolicy')
 * @param locale - The locale (e.g., 'fr')
 */
function getLocalizedPath(key: string, locale: string): string {
  return (
    LOCALIZED_URLS[key]?.[locale] ?? LOCALIZED_URLS[key]?.[DEFAULT_LOCALE] ?? ''
  );
}

/**
 * Application URLs - dynamically built based on current environment
 * @param locale - Locale for URL path translation (e.g., 'fr')
 */
export const getLoginRedirectUrl = (locale: string) =>
  buildAppUrl(LOGIN_URL, locale);
/**
 * Build the logout callback URL that clears the session before redirecting to login.
 * Used on MFA screens where a direct redirect would loop back to the same screen.
 * @param locale - Locale for URL path translation (e.g., 'fr')
 */
export const getLogoutCallbackUrl = (locale: string) => {
  const baseUrl = getAppBaseUrl();
  const returnTo = encodeURIComponent(getLoginRedirectUrl(locale));
  return `${baseUrl}${LOGOUT_CALLBACK_URL}?redirect_uri=${returnTo}`;
};
export const getTermsAndConditionsUrl = (locale: string) =>
  buildAppUrl(getLocalizedPath('termsAndConditions', locale), locale);
export const getPrivacyPolicyUrl = (locale: string) =>
  buildAppUrl(getLocalizedPath('privacyPolicy', locale), locale);

/**
 * Build the URL of the consent versions endpoint exposed by the shell.
 *
 * The shell proxies the values from the Magnolia `engie-consent` workspace
 * at a stable path (`/next/content-api/consent/version`). It replaces the
 * previous DAM-hosted `versions.json` whose URL kept changing because S3
 * bakes a content hash into asset URLs. The `/next/content-api/` prefix
 * is the shell's own namespace — `/next/api/` is reserved upstream for
 * Kaluza-backed calls.
 */
export const getConsentManifestUrl = (): string =>
  `${getAppBaseUrl()}/next/content-api/consent/version`;
